import { useCallback, useEffect, useMemo, useRef } from 'react';

export interface PilotReplayCursorSnapshot {
  interactionStateId: string;
  lastSeqId: number;
  updatedAt: string;
}

interface UsePilotReplayCursorOptions {
  activityId?: string | null;
  phase?: string | null;
  interactionStateId?: string | null;
  isStreamingInteraction?: boolean;
  onInteractionStateChanged?: (
    previousInteractionStateId: string | null,
    nextInteractionStateId: string,
  ) => void;
}

const PILOT_REPLAY_CURSOR_STORAGE_PREFIX = 'spassu-code:pilot-replay-cursor';

function parseSnapshot(raw: string | null): PilotReplayCursorSnapshot | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PilotReplayCursorSnapshot>;
    if (
      typeof parsed.interactionStateId !== 'string' ||
      typeof parsed.lastSeqId !== 'number' ||
      typeof parsed.updatedAt !== 'string'
    ) {
      return null;
    }

    return {
      interactionStateId: parsed.interactionStateId,
      lastSeqId: parsed.lastSeqId,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

export function getPilotReplayCursorStorageKey(
  activityId: string,
  phase: string,
): string {
  return `${PILOT_REPLAY_CURSOR_STORAGE_PREFIX}:${activityId}:${phase}`;
}

export function usePilotReplayCursor({
  activityId,
  phase,
  interactionStateId,
  isStreamingInteraction = true,
  onInteractionStateChanged,
}: UsePilotReplayCursorOptions) {
  const lastSeqIdRef = useRef(0);
  const lastInteractionStateIdRef = useRef<string | null>(null);
  const storageKey = useMemo(
    () =>
      activityId && phase
        ? getPilotReplayCursorStorageKey(activityId, phase)
        : null,
    [activityId, phase],
  );

  const readSnapshot = useCallback((): PilotReplayCursorSnapshot | null => {
    if (!storageKey) {
      return null;
    }

    try {
      return parseSnapshot(sessionStorage.getItem(storageKey));
    } catch {
      return null;
    }
  }, [storageKey]);

  const clearCursor = useCallback(() => {
    lastSeqIdRef.current = 0;
    if (!storageKey) {
      return;
    }

    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Ignore sessionStorage errors in restricted environments.
    }
  }, [storageKey]);

  const writeCursor = useCallback(
    (nextInteractionStateId: string, nextLastSeqId: number) => {
      lastSeqIdRef.current = nextLastSeqId;

      if (!storageKey) {
        return;
      }

      const snapshot: PilotReplayCursorSnapshot = {
        interactionStateId: nextInteractionStateId,
        lastSeqId: nextLastSeqId,
        updatedAt: new Date().toISOString(),
      };

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
      } catch {
        // Ignore sessionStorage errors in restricted environments.
      }
    },
    [storageKey],
  );

  const onSeqId = useCallback(
    (seqId: number) => {
      lastSeqIdRef.current = seqId;

      if (!interactionStateId || !isStreamingInteraction) {
        return;
      }

      writeCursor(interactionStateId, seqId);
    },
    [interactionStateId, isStreamingInteraction, writeCursor],
  );

  useEffect(() => {
    if (!storageKey) {
      lastInteractionStateIdRef.current = null;
      lastSeqIdRef.current = 0;
      return;
    }

    if (!interactionStateId) {
      if (lastInteractionStateIdRef.current) {
        clearCursor();
      } else {
        lastSeqIdRef.current = 0;
      }
      lastInteractionStateIdRef.current = null;
      return;
    }

    if (!isStreamingInteraction) {
      const snapshot = readSnapshot();
      const previousInteractionStateId =
        snapshot?.interactionStateId ?? lastInteractionStateIdRef.current;

      if (
        previousInteractionStateId &&
        previousInteractionStateId !== interactionStateId
      ) {
        onInteractionStateChanged?.(
          previousInteractionStateId,
          interactionStateId,
        );
      }

      clearCursor();
      lastInteractionStateIdRef.current = interactionStateId;
      return;
    }

    const snapshot = readSnapshot();
    if (snapshot?.interactionStateId === interactionStateId) {
      lastSeqIdRef.current = snapshot.lastSeqId;
      lastInteractionStateIdRef.current = interactionStateId;
      return;
    }

    const previousInteractionStateId =
      snapshot?.interactionStateId ?? lastInteractionStateIdRef.current;
    if (
      previousInteractionStateId &&
      previousInteractionStateId !== interactionStateId
    ) {
      onInteractionStateChanged?.(
        previousInteractionStateId,
        interactionStateId,
      );
    }

    writeCursor(interactionStateId, 0);
    lastInteractionStateIdRef.current = interactionStateId;
  }, [
    isStreamingInteraction,
    interactionStateId,
    onInteractionStateChanged,
    readSnapshot,
    storageKey,
    clearCursor,
    writeCursor,
  ]);

  return {
    lastSeqIdRef,
    clearCursor,
    onSeqId,
    readSnapshot,
  };
}
